import {
  ConfigFilterOption,
  ConfigSort,
  useFilters,
  useSortBy,
} from "@usereactify/search";
import React, {
  Context,
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from "react";

import { useSite } from "~/hooks/useSite";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";

type FilterProviderProps = {
  children: ReactNode;
  gender: string;
  hideSizeFilter: boolean;
  collectionTitle: string;
};

export type FilterContext = {
  gender: "men" | "women";
  filters: ConfigFilterOption[];
  filtersSelected: {
    handle: string;
    value:
      | string
      | string[]
      | {
          label: string;
          start: number;
          end: number;
        }[];
  }[];
  sortOption: ConfigSort | undefined;
  sortOptions: ConfigSort[];
  setSortOption: (handle: string) => void;
  setShowMore: Dispatch<SetStateAction<boolean>>;
  handleScroll: () => void;
  showMore: boolean;
  showViewToggle: boolean;
};

const getActiveFilters = (
  filters: ConfigFilterOption[],
  isSale: boolean,
  hideSizeFilter: boolean,
  currencyCode: string | undefined,
  collectionTitle: string,
) =>
  filters?.filter((filter) => {
    if (hideSizeFilter && filter.handle === "size") {
      return false;
    }

    if (collectionTitle?.includes("Shoe Care") && filter.handle == "colour") {
      return false;
    }

    if (
      collectionTitle?.includes("Shoe Care") &&
      filter.handle == "subcategory"
    ) {
      return false;
    }

    if (filter.handle === "spring-sale") {
      return false;
    }

    if (isSale && filter.handle === `sale-price-${currencyCode}`) {
      return true;
    }

    if (!isSale && filter.handle === "discount") {
      return false;
    }

    if (
      isSale &&
      (filter.handle.startsWith("sale-price") || filter.handle === "price")
    ) {
      return false;
    }

    if (!isSale && filter.handle.startsWith("sale-")) {
      return false;
    }

    return true;
  });

export const FilterContext: Context<FilterContext> =
  createContext<FilterContext>({
    gender: "women",
    filters: [],
    filtersSelected: [],
    sortOption: undefined,
    sortOptions: [],
    setSortOption: () => {},
    setShowMore: () => {},
    showMore: false,
    handleScroll: () => {},
    showViewToggle: true,
  });

const shouldShowViewToggle = (collectionTitle: string) => {
  // Return false for specific collections where we don't want to show the toggle
  return !(
    collectionTitle?.includes("Shoe Care") ||
    collectionTitle?.includes("Pet") ||
    collectionTitle?.includes("Socks & Accessories")
  );
};

export const FilterProvider = ({
  children,
  gender,
  hideSizeFilter,
  collectionTitle,
}: FilterProviderProps) => {
  const { currencyCode } = useSite();
  const { filters: reactifyFilters, selected: filtersSelected } = useFilters();

  const { enableSale } = useRouteLoaderData("routes/$site");

  const {
    sortOption,
    sortOptions: reactifySortOptions,
    setSortOption,
  } = useSortBy();

  const [showMore, setShowMore] = useState(false);

  const handleScroll = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  const filters = useMemo(
    () =>
      getActiveFilters(
        reactifyFilters || [],
        enableSale,
        hideSizeFilter,
        currencyCode,
        collectionTitle,
      ),
    [
      reactifyFilters,
      enableSale,
      hideSizeFilter,
      currencyCode,
      collectionTitle,
    ],
  );

  /**
   * Sort options normally target the ordinary price fields but during sale, we
   * show special options which target the sale price metafields.
   *
   * Outside of sale, only the normal price options are displayed.
   * During sale, only the special sale price options are displayed.
   */
  const sortOptions = useMemo(
    () =>
      (reactifySortOptions ?? [])
        .filter((sortOption) => {
          // Sort option is unrelated to price, allow it.
          if (!sortOption.field.includes("price")) return true;

          // Is this sort option targetting sale prices?
          const isSalePriceOption = sortOption.field.includes("sale_price");

          // This option is for a sale price.
          if (true === isSalePriceOption) {
            // We are not on sale, so always hide this option.
            if (false === enableSale) return false;

            // This is a sale price option, but not for our currency.
            if (
              false ===
              sortOption.field.includes(
                `sale_price_${currencyCode.toLowerCase()}`,
              )
            ) {
              return false;
            }
          }

          // This option is for a normal price.
          if (false === isSalePriceOption) {
            // We are on sale, so always hide this option.
            if (true === enableSale) return false;
          }

          return true;
        })
        .map((sortOption) => ({
          ...sortOption,
          // Given a name like "Lowest Price [AUD Sale]", remove anything after
          // the opening bracket.
          name: sortOption.name.split("[")[0].trim(),
        }))
        // Sort by position.
        .sort((a, b) => (a.position < b.position ? -1 : 1)),
    [reactifySortOptions, enableSale, currencyCode],
  );

  const values = useMemo(
    () => ({
      gender,
      filters: filters || [],
      filtersSelected: filtersSelected || [],
      sortOption,
      sortOptions,
      setSortOption,
      setShowMore,
      showMore,
      handleScroll,
      showViewToggle: shouldShowViewToggle(collectionTitle),
    }),
    [
      gender,
      filters,
      filtersSelected,
      sortOption,
      sortOptions,
      setSortOption,
      setShowMore,
      showMore,
      handleScroll,
      collectionTitle,
    ],
  );

  return (
    <FilterContext.Provider value={values}>{children}</FilterContext.Provider>
  );
};
